import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Menu } from "@msuite/ui/menu";
import { ModalWrapper } from "@msuite/ui/modal";
import { useQueryClient } from "@msuite/ui/query";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import type { Contact } from "@msuite/utils/types";
import React from "react";
import { useNavigate } from "react-router";
import { useCitadel } from "@/hooks/use-citadel";
import { EditContactModal } from "@/modals/contacts/edit-contact-modal";

export function RowActions({ cell }: CellContext<Contact, "actions">) {
	/** Context */
	const { queryKey } = useDataTableContext();

	/** Hooks */
	const { t } = useTranslation();
	const { citadel } = useCitadel();
	const qc = useQueryClient();
	const editContactModal = useDisclosure();
	const contactId = cell.row.original.id;
	const navigate = useNavigate();

	/** Functions */
	async function handleOnCompleteEdit() {
		if (!queryKey) return;
		await qc.invalidateQueries({ queryKey });
	}

	function handleOnNavigateToContact() {
		const contactId = cell.row.original.id;
		if (!contactId) return;
		navigate(`/contacts/${contactId}`);
	}

	/** Computed */
	// The detail page guards the same modal; this second opener did not. Three states, so the
	// entry disappears on a definitive denial only.
	const editIsDenied = !!citadel && citadel.cannot("update", "Contact", { ...cell.row.original });

	/** Render */
	return (
		<React.Fragment>
			<RowActionsWrapper to={`/contacts/${contactId}`}>
				<Menu
					items={() => [
						{
							label: t("general.open"),
							icon: icon("Info"),
							onClick: handleOnNavigateToContact,
							cyId: "row-actions-open-button",
						},
						editIsDenied
							? null
							: {
									label: t("contacts.edit_contact"),
									onClick: editContactModal.onOpen,
									icon: icon("Edit"),
								},
					]}
				>
					<Button
						size="xxs"
						icon={<Icon name="EllipsisVertical" />}
						variant="ghost"
						data-cy="row-actions-button"
					/>
				</Menu>
			</RowActionsWrapper>
			<ModalWrapper>
				{editContactModal.isOpen && (
					<EditContactModal
						isOpen={editContactModal.isOpen}
						onClose={editContactModal.onClose}
						contactId={contactId}
						onComplete={handleOnCompleteEdit}
					/>
				)}
			</ModalWrapper>
		</React.Fragment>
	);
}
